import { Clock, MapPin, Users } from "lucide-react";

const DAYS = ["lundis", "mardis", "mercredis", "jeudis", "vendredis", "samedis", "dimanches"];

const PriceLine = ({ course, season }) => {
  const items = [[course.price_month, "mois"], [course.price_quarter, "trimestre"], [course.price, "an"]].filter(([v]) => Number(v) > 0);
  if (!items.length) return null;
  const pcts = season?.sibling_discount_pcts || [];
  const fee = Number(season?.membership_fee || 0);
  return (
    <div className="space-y-1" data-testid="course-prices">
      <div className="flex items-baseline gap-x-3 gap-y-1 flex-wrap text-xs">
        <span className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Tarifs</span>
        {items.map(([v, per]) => <span key={per}><span className="font-display text-xl text-white">{Number(v).toFixed(0)} €</span><span className="text-[color:var(--text-muted)]"> / {per}</span></span>)}
      </div>
      <div className="text-[10px] text-[color:var(--text-muted)] flex flex-wrap gap-x-2" data-testid="course-price-notes">
        {fee > 0 && <span>Adhésion {fee.toFixed(0)} €/enfant (une fois)</span>}
        {(season?.first_class_free ?? true) && <span>· 1er cours offert</span>}
        {pcts.length > 0 && <span className="text-green-300" data-testid="course-sibling-discount">· Fratrie −{Number(pcts[0])} % (2ᵉ){pcts[1] !== undefined ? `, −${Number(pcts[1])} % (3ᵉ+)` : ""}</span>}
      </div>
    </div>
  );
};

const RemainingPill = ({ remaining }) => {
  const full = remaining <= 0;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold ${full ? "bg-red-500/20 text-red-300" : "bg-black/60 backdrop-blur text-[color:var(--accent)]"}`} data-testid="course-remaining">
      <Users className="w-3 h-3" /> {full ? "Complet" : `${remaining} place${remaining > 1 ? "s" : ""} restante${remaining > 1 ? "s" : ""}`}
    </span>
  );
};

export const CourseCard = ({ course, onEnroll, season }) => (
  <article className="card-hover rounded-2xl overflow-hidden border border-[color:var(--border)] bg-[color:var(--bg-elev)] flex flex-col" data-testid={`course-card-${course.id}`}>
    <div className="relative aspect-[4/5] bg-black overflow-hidden">
      {course.flyer_url
        ? <img src={course.flyer_url} alt={`Flyer du cours ${course.name}`} className="w-full h-full object-cover object-top" loading="lazy" />
        : <div className="w-full h-full" style={{ background: `linear-gradient(160deg, ${course.color}, #000)` }} />}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
      <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold text-black" style={{ background: course.color }}>
        Tous les {DAYS[course.weekday]}
      </div>
      <div className="absolute top-3 right-3"><RemainingPill remaining={course.remaining} /></div>
      <div className="absolute bottom-4 left-5 right-5">
        <h3 className="font-display text-4xl leading-none text-white">{course.name}</h3>
        <div className="text-xs text-white/80 mt-1 flex items-center gap-2 flex-wrap">
          <span>Avec {course.teacher}</span>
          {course.room && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {course.room}</span>}
        </div>
      </div>
    </div>
    <div className="p-5 flex-1 flex flex-col gap-4">
      <ul className="space-y-2">
        {(course.slots || []).map((s, i) => (
          <li key={`${s.start}-${i}`} className="flex items-center gap-3 text-sm">
            <span className="inline-flex items-center gap-1.5 font-mono text-[color:var(--accent)] tabular-nums"><Clock className="w-3.5 h-3.5" /> {s.start} — {s.end}</span>
            <span className="text-[color:var(--text)]">{s.level}</span>
            {s.age && <span className="text-xs text-[color:var(--text-muted)]">({s.age})</span>}
          </li>
        ))}
      </ul>
      {course.description && <p className="text-xs text-[color:var(--text-muted)] leading-relaxed">{course.description}</p>}
      <PriceLine course={course} season={season} />
      <button
        type="button"
        onClick={() => onEnroll(course)}
        disabled={course.remaining <= 0}
        data-testid={`course-enroll-${course.id}`}
        className="mt-auto inline-flex items-center justify-center gap-2 bg-[color:var(--accent)] text-black font-semibold px-5 py-2.5 rounded-full text-xs uppercase tracking-widest hover:brightness-110 transition disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {course.remaining <= 0 ? "Liste d'attente" : "Réserver une place"}
      </button>
    </div>
  </article>
);
